import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { Mail, Lock, Eye, EyeOff } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import AuthShell from '../../components/AuthShell'

export default function LoginPage() {
  const { signInWithEmail, signInWithGoogle } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail]               = useState('')
  const [password, setPassword]         = useState('')
  const [showPass, setShowPass]         = useState(false)
  const [loading, setLoading]           = useState(false)
  const [error, setError]               = useState('')
  const [forgotMode, setForgotMode]     = useState(false)
  const [forgotEmail, setForgotEmail]   = useState('')
  const [forgotSent, setForgotSent]     = useState(false)
  const [forgotLoading, setForgotLoading] = useState(false)

  async function handleForgotPassword(e) {
    e.preventDefault()
    setForgotLoading(true)
    await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: `${window.location.origin}/auth/callback`
    })
    setForgotSent(true)
    setForgotLoading(false)
  }

  async function handleEmailLogin(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await signInWithEmail(email, password)
      navigate('/dashboard')
    } catch (err) {
      setError(err.message || 'Login failed. Please check your details.')
    } finally { setLoading(false) }
  }

  async function handleGoogleLogin() {
    setError('')
    try { await signInWithGoogle() }
    catch (err) { setError('Could not sign in with Google. Try again.') }
  }

  return (
    <>
      <AuthShell image="auth-embrace" tagline="Welcome back to your love story." sub="Every detail, right where you left it.">

        {/* Logo */}
        <Link to="/" style={{ textDecoration:'none', marginBottom:'2.5rem', display:'block' }}>
          <img src="/okoiya-logo.png" alt="Okoiya" style={{ height:52, display:'block', margin:'0 auto' }}/>
        </Link>

        <div className="card" style={{ width:'100%', maxWidth:420, padding:'2.5rem', boxShadow:'0 24px 64px rgba(122,60,40,0.10)' }}>
          <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.6rem', color:'#1A1A1A', marginBottom:'0.4rem', textAlign:'center' }}>
            Welcome back
          </h2>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'#6B6B6B', textAlign:'center', marginBottom:'2rem' }}>
            Sign in to continue your wedding journey
          </p>

          {/* Google Button */}
          <button onClick={handleGoogleLogin} style={{
            width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.75rem',
            background:'white', border:'1.5px solid #E5E0D8', borderRadius:'0.75rem',
            padding:'0.75rem', fontFamily:'Nunito Sans, sans-serif', fontWeight:600, fontSize:'0.9rem',
            color:'#1A1A1A', cursor:'pointer', marginBottom:'1.25rem', transition:'all 0.2s'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'1.25rem' }}>
            <div style={{ flex:1, height:1, background:'#E5E0D8' }}/>
            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#BCBCBC' }}>or</span>
            <div style={{ flex:1, height:1, background:'#E5E0D8' }}/>
          </div>

          {/* Email Form */}
          <form onSubmit={handleEmailLogin}>
            <div style={{ marginBottom:'1rem' }}>
              <label className="label">Email Address</label>
              <div style={{ position:'relative' }}>
                <Mail size={15} color="#BCBCBC" style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)' }}/>
                <input className="input-field" style={{ paddingLeft:'2.25rem' }} type="email" placeholder="you@example.com" value={email} onChange={e=>setEmail(e.target.value)} required/>
              </div>
            </div>

            <div style={{ marginBottom:'1.5rem' }}>
              <label className="label">Password</label>
              <div style={{ position:'relative' }}>
                <Lock size={15} color="#BCBCBC" style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)' }}/>
                <input className="input-field" style={{ paddingLeft:'2.25rem', paddingRight:'2.5rem' }} type={showPass?'text':'password'} placeholder="Your password" value={password} onChange={e=>setPassword(e.target.value)} required/>
                <button type="button" onClick={()=>setShowPass(!showPass)} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', padding:0 }}>
                  {showPass ? <EyeOff size={15} color="#BCBCBC"/> : <Eye size={15} color="#BCBCBC"/>}
                </button>
              </div>
              <button type="button" onClick={()=>setForgotMode(true)}
                style={{ background:'none', border:'none', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#C4826A', padding:0, textAlign:'right', width:'100%', marginTop:'0.4rem' }}>
                Forgot password?
              </button>
            </div>

            {error && (
              <div style={{ background:'rgba(196,130,106,0.1)', border:'1px solid rgba(196,130,106,0.3)', borderRadius:'0.5rem', padding:'0.65rem 0.875rem', marginBottom:'1rem' }}>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#A86650', margin:0 }}>{error}</p>
              </div>
            )}

            <button type="submit" className="btn-primary" style={{ width:'100%', justifyContent:'center' }} disabled={loading}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#6B6B6B', textAlign:'center', marginTop:'1.5rem' }}>
            Don't have an account?{' '}
            <Link to="/signup" style={{ color:'#C4826A', fontWeight:600, textDecoration:'none' }}>Create one free</Link>
          </p>
        </div>
      </AuthShell>

      {/* Forgot password modal */}
      {forgotMode && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100, padding:'1rem' }}>
          <div style={{ background:'white', borderRadius:'1.25rem', padding:'1.75rem', width:'100%', maxWidth:360, boxShadow:'0 16px 48px rgba(0,0,0,0.15)' }}>
            {forgotSent ? (
              <>
                <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.2rem', color:'#1A1A1A', margin:'0 0 0.5rem' }}>Check your inbox</p>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#6B6B6B', margin:'0 0 1.25rem', lineHeight:1.7 }}>We sent a reset link to {forgotEmail}.</p>
                <button onClick={()=>{ setForgotMode(false); setForgotSent(false); setForgotEmail('') }} className="btn-primary" style={{ width:'100%' }}>Done</button>
              </>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.2rem', color:'#1A1A1A', margin:'0 0 0.25rem' }}>Reset your password</p>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#9B9B9B', margin:'0 0 1.25rem' }}>Enter your email and we'll send a reset link.</p>
                <input className="input-field" type="email" placeholder="your@email.com" value={forgotEmail} onChange={e=>setForgotEmail(e.target.value)} required style={{ marginBottom:'0.875rem' }}/>
                <div style={{ display:'flex', gap:'0.5rem' }}>
                  <button type="button" onClick={()=>setForgotMode(false)} className="btn-secondary" style={{ flex:1 }}>Cancel</button>
                  <button type="submit" disabled={forgotLoading} className="btn-primary" style={{ flex:1 }}>{forgotLoading?'Sending…':'Send Link'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  )
}
